/* global ShowcaseRedesign */
import React from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import Icon from 'components/Icon';
import {
  isClip,
  isLive,
  isTypeSeries,
  isEpisode,
  isMovie,
  isSpecial,
  isTypePromo,
  isTypeVideo,
  truncateText,
} from 'helpers/ContentHelper';
import {
  Events,
  getSegmentModelMetadata,
  getTealiumRedesignEvents,
  handleTealiumButtonAction,
  SEGMENT_HERO_COLLECTION_TITLE,
  handleAnalytics,
} from '@foxcorp/delta-web-analytics';
import { helperGetLiveUrl, helperGetRelativeUrl } from 'helpers/RouteHelper';
import { RESTART_TYPE_HOME } from 'screens/Watch/WatchHelpers';

const SHOWCASE_LIVE = 'SHOWCASE_LIVE';
const SHOWCASE_MOVIE = 'SHOWCASE_MOVIE';
const SHOWCASE_SERIES = 'SHOWCASE_SERIES';
const SHOWCASE_SERIES_EPISODE = 'SHOWCASE_SERIES_EPISODE';
const SHOWCASE_SPECIAL = 'SHOWCASE_SPECIAL';
const SHOWCASE_UPCOMING = 'SHOWCASE_UPCOMING';
const SHOWCASE_CLIP = 'SHOWCASE_CLIP';
const SHOWCASE_PROMO = 'SHOWCASE_PROMO';
const BUTTON_TYPE = {
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY',
  TRETIARY: 'TRETIARY',
};

export const getShowcaseType = item => {
  const { secUntilStart, seriesType, type, videoType } = item;

  if (isLive(type)) {
    const startsInFuture = secUntilStart && secUntilStart > 0;
    if (startsInFuture) return SHOWCASE_UPCOMING;
    return SHOWCASE_LIVE;
  }
  if (isTypeSeries(type)) return SHOWCASE_SERIES;
  if (isTypePromo(type)) return SHOWCASE_PROMO;
  if (isTypeVideo(type)) {
    if (isClip(videoType)) return SHOWCASE_CLIP;
    if (isMovie(videoType)) return SHOWCASE_MOVIE;
    if (isEpisode(videoType)) {
      if (isSpecial(seriesType)) return SHOWCASE_SPECIAL;
      return SHOWCASE_SERIES_EPISODE;
    }
  }
  return null;
};

export function buildInfoTags(props) {
  const {
    canDisplayRestartButton,
    foxAwesomeLayout,
    isDescriptionOpen,
    item = {},
    style,
    toggleDescription,
    collectionInfo,
    headerImageState,
    idx,
    account,
  } = props;

  const {
    callSign,
    clipMetaData,
    description,
    isLive: videoHasLiveUrl,
    isSeriesDetailAvailable,
    liveMetaData,
    liveVideoEnded,
    lockButtons,
    metadata,
    movieMetaData,
    name,
    network,
    networkIcon,
    originalAirDateMetaData,
    percentWatched,
    playerScreenUrl,
    seasonEpisodeMetaData,
    secUntilStart,
    seriesName,
    seriesSeasonEpisode,
    subtitle,
    tabIndex,
    titleLink,
    upcomingMetadata,
    watchLink,
    videoId,
    title = '',
  } = item;
  const isRestartEnabled =
    typeof item?.originalVideo?.restartEnabled !== 'undefined'
      ? item?.originalVideo?.restartEnabled
      : canDisplayRestartButton;
  const displaySeasonAndEpisodeCounts = _.get(item, 'displaySeasonAndEpisodeCounts', true);
  const {
    global_moreClips: moreClipsText,
    global_moreInfoButton: moreInfoText,
    global_moreSeriesDeeplinkButton: moreEpisodesText,
    global_moreButton: descriptionMoreText,
    global_lessButton: descriptionLessText,
    global_playNowLabel: playNowText,
    global_restartButton: restartText,
    global_startWatchingButton: startWatchingButtonText,
    global_watchLiveButton: watchLiveText,
    showcase_watchButton: showcaseWatchNowText,
    global_watchButton: watchButtonText,
  } = window.Language;

  let highlightText;
  let highlightClass;
  let isRestartBtn;
  let isWatchBtn = true;
  let isResumeBtn = percentWatched && percentWatched > 0 && percentWatched < 100;
  let isWatchLiveSecondaryBtn;
  let isRestartSecondaryBtn;
  let isPreviewSecondaryBtn;
  let primaryButtonText;
  let primaryButtonIcon;
  let primaryButtonViewBox;
  let primaryButtonLink;
  let primaryButtonIsPlayButton = false;
  let secondaryButtonLink;
  let secondaryButtonText;
  let tertiaryButtonLink;
  let tertiaryButtonText;
  let meta = metadata;
  let showName = seriesName;
  let showProgressBar = false;
  const secondaryButtonClasses = [foxAwesomeLayout ? style.foxAwesomeMoreLink : style.moreLink];
  const tertiaryButtonClasses = [foxAwesomeLayout ? style.foxAwesomeMoreLink : style.moreLink];
  if (ShowcaseRedesign) {
    secondaryButtonClasses.push(style.squared);
    tertiaryButtonClasses.push(style.squared);
  }
  const descriptionClasses = [style.descriptionText];
  if (lockButtons) {
    primaryButtonIcon = 'lock';
    primaryButtonViewBox = '-7 -1 30 30';
  }
  const networkLogo = networkIcon && (
    <img
      className={style.networkLogo}
      data-test="info-tags-network-logo"
      src={networkIcon}
      alt={network}
    />
  );
  const showcaseType = getShowcaseType(item);
  const detailPageAvailable = titleLink && isSeriesDetailAvailable !== false;
  switch (showcaseType) {
    case SHOWCASE_LIVE: {
      if (videoHasLiveUrl && !liveVideoEnded) {
        primaryButtonText = watchLiveText;
        primaryButtonIcon = lockButtons ? 'lock' : 'play-rectangle';
        primaryButtonLink = helperGetLiveUrl({ callSign, videoId });
        primaryButtonIsPlayButton = true;
        if (playerScreenUrl && isRestartEnabled) {
          secondaryButtonText = restartText;
          const restartLink = helperGetRelativeUrl({ url: playerScreenUrl });
          secondaryButtonLink = `${restartLink}#${RESTART_TYPE_HOME}`;
          isRestartSecondaryBtn = true;
          if (detailPageAvailable) {
            tertiaryButtonText = moreInfoText;
            tertiaryButtonLink = titleLink;
          }
        } else if (detailPageAvailable) {
          secondaryButtonText = moreInfoText;
          secondaryButtonLink = titleLink;
        }
      } else if (detailPageAvailable) {
        primaryButtonText = moreInfoText;
        primaryButtonLink = titleLink;
      }
      isRestartBtn = false;
      isWatchBtn = false;
      isResumeBtn = false;
      descriptionClasses.push(style.descriptionTextVideo);
      highlightClass = style.highlight;
      highlightText = subtitle;
      meta = liveMetaData;
      showProgressBar = true;
      break;
    }
    case SHOWCASE_UPCOMING: {
      const videoStartsWithinFiveMinutes = secUntilStart && secUntilStart <= 300;
      const canPlay = videoHasLiveUrl && videoStartsWithinFiveMinutes;
      if (canPlay) {
        primaryButtonText = watchLiveText;
        primaryButtonIcon = lockButtons ? 'lock' : 'play-rectangle';
        primaryButtonLink = helperGetLiveUrl({ callSign, videoId });
        primaryButtonIsPlayButton = true;
        if (detailPageAvailable) {
          secondaryButtonText = moreInfoText;
          secondaryButtonLink = titleLink;
        }
      } else if (detailPageAvailable) {
        primaryButtonText = moreInfoText;
        primaryButtonLink = titleLink;
        primaryButtonIcon = null;
      }
      highlightText = subtitle;
      highlightClass = style.highlight;
      descriptionClasses.push(style.descriptionTextVideo);
      meta = upcomingMetadata;
      break;
    }
    case SHOWCASE_SERIES: {
      if (watchLink) {
        primaryButtonText = displaySeasonAndEpisodeCounts
          ? `${startWatchingButtonText} ${seriesSeasonEpisode}`
          : showcaseWatchNowText;
        primaryButtonIcon = lockButtons ? 'lock' : 'play-rectangle';
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = moreEpisodesText;
        secondaryButtonLink = titleLink;
      }
      showName = name;
      meta = seasonEpisodeMetaData;
      break;
    }
    case SHOWCASE_SERIES_EPISODE: {
      if (watchLink) {
        primaryButtonText = isResumeBtn ? 'Resume' : showcaseWatchNowText;
        primaryButtonIcon = lockButtons ? 'lock' : 'play-rectangle';
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      highlightText = subtitle;
      highlightClass = style.highlight;
      descriptionClasses.push(style.descriptionTextVideo);
      if (titleLink) {
        secondaryButtonText = moreEpisodesText;
        secondaryButtonLink = titleLink;
      }
      meta = originalAirDateMetaData;
      break;
    }
    case SHOWCASE_SPECIAL: {
      if (watchLink) {
        primaryButtonText = isResumeBtn ? 'Resume' : showcaseWatchNowText;
        primaryButtonIcon = lockButtons ? 'lock' : 'play-rectangle';
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonLink = titleLink;
        secondaryButtonText = moreInfoText;
      }
      meta = originalAirDateMetaData;
      break;
    }
    case SHOWCASE_PROMO: {
      if (watchLink) {
        primaryButtonText = playNowText;
        primaryButtonIcon = 'play-rectangle';
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = moreEpisodesText;
        secondaryButtonLink = titleLink;
      }
      meta = seasonEpisodeMetaData;
      break;
    }
    case SHOWCASE_MOVIE: {
      if (watchLink) {
        primaryButtonText = isResumeBtn ? 'Resume' : showcaseWatchNowText;
        primaryButtonIcon = lockButtons ? 'lock' : 'play-rectangle';
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = moreInfoText;
        secondaryButtonLink = titleLink;
      }
      meta = movieMetaData;
      break;
    }
    case SHOWCASE_CLIP: {
      if (watchLink) {
        primaryButtonText = isResumeBtn ? 'Resume' : showcaseWatchNowText;
        primaryButtonIcon = 'play-rectangle';
        primaryButtonLink = watchLink;
        primaryButtonIsPlayButton = true;
      }
      if (titleLink) {
        secondaryButtonText = moreClipsText;
        secondaryButtonLink = titleLink;
      }
      meta = clipMetaData;
      break;
    }
    default:
  }

  const descriptionWordCount = 112;
  const isDescriptionLong = description && description.length > descriptionWordCount;
  const descriptionText = isDescriptionOpen
    ? description
    : truncateText(description, descriptionWordCount);

  const buttonContainerClasses = [style.buttons];
  const descriptionButtonClasses = style.descriptionButton;
  const descriptionButtonText = isDescriptionOpen ? descriptionLessText : descriptionMoreText;

  const descriptionButton = isDescriptionLong ? (
    <button className={descriptionButtonClasses} onClick={toggleDescription} tabIndex={tabIndex}>
      {descriptionButtonText}
    </button>
  ) : null;

  const descriptionComponent = (
    <div className={classNames(descriptionClasses)} data-test="info-tags-description">
      {descriptionText}
      {descriptionButton}
    </div>
  );

  if (primaryButtonText && primaryButtonIcon) {
    primaryButtonText = [
      <Icon
        key="homePrimaryButtonIcon"
        className={primaryButtonIcon}
        viewBox={primaryButtonViewBox}
        icon={primaryButtonIcon}
        color="white"
        ariaHidden
      />,
      primaryButtonText,
    ];
  }

  const handleButtonClickAnalyticsEvent = (button = '') => {
    switch (button) {
      case BUTTON_TYPE.PRIMARY:
        handlePrimaryClickAnalytics();
        break;
      case BUTTON_TYPE.SECONDARY:
        handleSecondaryClickAnalytics();
        break;
      case BUTTON_TYPE.TRETIARY:
        handleTertiaryClickAnalytics();
        break;
      default:
    }
  };

  const handleSegmentItemClick = buttonText => {
    const payload = {
      page_action_type: buttonText,
      page_collection_count: collectionInfo.totalCollections,
      page_collection_id: collectionInfo.panelId,
      page_collection_title: SEGMENT_HERO_COLLECTION_TITLE,
      page_collection_type: collectionInfo.collectionType,
      page_item_type: 'hero',
      page_item_state: headerImageState,
      page_item_container_title: title,
      page_item_vertical_position: '1',
      page_item_horizontal_position: idx + 1,
      page_item_title: buttonText,
      page_item_subtitle: highlightText,
      page_item_cc: 'cc off',
      page_item_volume: 'volume off',
      page_playlist_length: collectionInfo.totalItems,
      page_recs_collection: collectionInfo.isPanelRecommended,
      page_video_uid: collectionInfo.uID,
      ...getSegmentModelMetadata(),
    };
    handleAnalytics({ event: Events.HERO_ITEM_SELECT_ACTION, payload });
  };

  const tealiumRedesignEvents = getTealiumRedesignEvents({ account });

  const tealiumCollectionEvents = ({ headline, collectionType, totalItems, totalCollections }) => {
    tealiumRedesignEvents.heroItemSelect({
      account,
      type: 'hero',
      state: 'autoplay video',
      containerTitle: name,
      verticalPosition: '1',
      horizontalPosition: idx + 1,
      title,
      subtitle: secondaryButtonText,
      cc: 'cc off',
      volume: 'volume off',
      collectionTitle: headline,
      collectionType,
      playlistLength: totalItems,
      collectionCount: totalCollections,
      item,
    });
  };

  const handlePrimaryClickAnalytics = () => {
    const buttonText = Array.isArray(primaryButtonText) ? primaryButtonText[1] : primaryButtonText;
    handleSegmentItemClick(buttonText);
    handleTealiumButtonAction({
      text: watchButtonText,
      title,
      subtitle: name,
      account,
      isRestartBtn,
      isWatchBtn,
    });
    tealiumCollectionEvents(collectionInfo);
  };

  const handleSecondaryClickAnalytics = () => {
    handleSegmentItemClick(tertiaryButtonText);
    handleTealiumButtonAction({
      text: secondaryButtonText,
      title,
      subtitle: name,
      account,
      isWatchLiveSecondaryBtn,
    });
    tealiumCollectionEvents(collectionInfo);
  };

  const handleTertiaryClickAnalytics = () => {
    handleSegmentItemClick(tertiaryButtonText);
    handleTealiumButtonAction({
      text: secondaryButtonText,
      title,
      subtitle: name,
      account,
      isWatchLiveSecondaryBtn,
    });
    tealiumCollectionEvents(collectionInfo);
  };

  return {
    buttonContainerClasses,
    descriptionComponent,
    highlightText,
    highlightClass,
    meta,
    networkLogo,
    showName,
    isRestartBtn,
    isWatchBtn,
    isResumeBtn,
    isWatchLiveSecondaryBtn,
    isRestartSecondaryBtn,
    isPreviewSecondaryBtn,
    primaryButtonText,
    primaryButtonLink,
    primaryButtonIsPlayButton,
    secondaryButtonLink,
    secondaryButtonText,
    secondaryButtonClasses,
    showProgressBar,
    tertiaryButtonLink,
    tertiaryButtonText,
    tertiaryButtonClasses,
    handleButtonClickAnalyticsEvent,
    BUTTON_TYPE,
  };
}
